/**
 * Parse the JSONL replay format:
 *   line 1 = ReplayEnvelope
 *   line 2..N = ReplayEvent
 *
 * We deliberately keep parsing tolerant — a corrupted line throws with a
 * useful message rather than silently dropping events.
 */

import { isKnownEventType } from "./stateReplayer.js";
import type { Replay, ReplayEnvelope, ReplayEvent } from "./types.js";

export function parseReplayText(text: string, filename: string): Replay {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    throw new Error(`${filename}: empty file`);
  }
  let envelope: ReplayEnvelope;
  try {
    envelope = JSON.parse(lines[0]!) as ReplayEnvelope;
  } catch (err) {
    throw new Error(`${filename}: bad envelope on line 1 — ${String(err)}`);
  }
  if (envelope.schema !== "replay/v0.1") {
    throw new Error(
      `${filename}: unsupported schema "${(envelope as { schema?: string }).schema}"`,
    );
  }
  const events: ReplayEvent[] = [];
  const warnings: string[] = [];
  for (let i = 1; i < lines.length; i++) {
    try {
      events.push(JSON.parse(lines[i]!) as ReplayEvent);
    } catch (err) {
      throw new Error(`${filename}: bad event JSON on line ${i + 1} — ${String(err)}`);
    }
  }
  // Sanity: events sorted by seq.
  for (let i = 1; i < events.length; i++) {
    if (events[i]!.seq === events[i - 1]!.seq) {
      throw new Error(`${filename}: duplicate seq ${events[i]!.seq}`);
    }
    if (events[i]!.seq < events[i - 1]!.seq) {
      throw new Error(
        `${filename}: events out of order at seq ${events[i]!.seq}`,
      );
    }
  }
  for (const event of events) {
    if (!isKnownEventType(event.type)) {
      warnings.push(
        `${filename}: unknown event type ${event.type} at seq ${event.seq}`,
      );
    }
    if (
      !envelope.spectator_events_included &&
      event.spectator_only === true
    ) {
      warnings.push(
        `${filename}: spectator-only event seq ${event.seq} appears in a public replay`,
      );
    }
  }
  return { envelope, events, filename, warnings };
}

export async function parseReplayFile(file: File): Promise<Replay> {
  const text = await file.text();
  return parseReplayText(text, file.name);
}
